import { useState } from 'react';
import { ReactComponent as editIcon } from '@epam/assets/icons/common/content-edit-18.svg';
import { ReactComponent as deleteIcon } from '@epam/assets/icons/common/action-delete-18.svg';
import { Button, FlexCell, FlexRow, TextInput, Text } from '@epam/promo';
import { useUuiContext } from '@epam/uui-core';
import { useModal } from 'hooks/ui/useModal';
import {
  useGetBroadcastTagsQuery,
  useCreateBroadcastTagQuery,
  useUpdateBroadcastTagQuery,
  useDeleteBroadcastTagQuery,
} from 'hooks/api/appSettings';

import css from './BroadcastTagsPage.module.scss';
import { SkeletonPage } from '../../../components/common';
import { BroadcastTag } from '../../../models/broadcastTag.model';
import { EditTagModal } from './EditTagModal';

export const BroadcastTagsPage = () => {
  const { tags, isLoading } = useGetBroadcastTagsQuery();
  const { mutate: createBroadcastTag } = useCreateBroadcastTagQuery();
  const { mutate: updateBroadcastTag } = useUpdateBroadcastTagQuery();
  const { mutate: deleteBroadcastTag } = useDeleteBroadcastTagQuery();
  const [inputValue, setInputValue] = useState('');
  const { uuiModals } = useUuiContext();
  const modal = useModal();

  const handleAdd = () => {
    const newTagNames = inputValue
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    newTagNames.forEach((tag) => createBroadcastTag({ name: tag, order: 0 }));

    setInputValue('');
  };

  const handleEdit = (tag: BroadcastTag) => {
    uuiModals
      .show((modalProps) => (
        <EditTagModal
          modalProps={modalProps}
          tag={tag}
          onUpdate={updateBroadcastTag}
        />
      ))
      .catch((e) => console.error(e));
  };

  const handleDelete = (tag: BroadcastTag) => {
    modal.delete({
      text: `Are you sure you want to delete tag "${tag.name}"?`,
      highlights: [`"${tag.name}"`, 'delete'],
      successAction: 'Delete',
      abortAction: 'Cancel',
      onSuccess: () => {
        deleteBroadcastTag(tag.id);
      },
    });
  };

  if (isLoading) {
    return <SkeletonPage />;
  }

  return (
    <FlexCell cx={css.container}>
      <FlexRow columnGap="12" cx={css.addRow} justifyContent="space-between">
        <FlexCell width="100%">
          <TextInput
            placeholder="Enter tag name(s), comma separated"
            value={inputValue}
            onValueChange={(val) => setInputValue(val ?? '')}
          />
        </FlexCell>

        <Button caption="Add tags" cx={css.addButton} onClick={handleAdd} />
      </FlexRow>

      <FlexRow
        cx={css.tagsList}
        rawProps={{ style: { flexWrap: 'wrap', gap: '12px' } }}>
        {tags && tags.length > 0 ? (
          tags.map((tag) => (
            <FlexRow key={tag.id} columnGap="6" cx={css.tagItem}>
              <Text>{tag.name}</Text>
              <Button
                fill="light"
                icon={editIcon}
                size="24"
                onClick={() => handleEdit(tag)}
              />
              <Button
                color="red"
                fill="light"
                icon={deleteIcon}
                size="24"
                onClick={() => handleDelete(tag)}
              />
            </FlexRow>
          ))
        ) : (
          <Text>No Tags available</Text>
        )}
      </FlexRow>
    </FlexCell>
  );
};
